'use client';

import { Select } from 'radix-ui';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from './cx';

export type KOption = {
  value: string;
  /** Подпись пункта; узел — когда слово со значком или числом (PopSelect на телефоне). */
  label: ReactNode;
  /** Вторая строка пункта — короткое пояснение. Видима всегда, не по наведению. */
  hint?: string;
  icon?: ReactNode;
  disabled?: boolean;
  /** Выделенный пункт («Своё» первым, QUICK-START §11.7 п.2): мягкая подложка `--accent-soft`, слово `--accent-stroke`. */
  accent?: boolean;
};

type Props = {
  value?: string;
  onChange?: (value: string) => void;
  options: KOption[];
  /** Текст, пока ничего не выбрано. */
  placeholder?: string;
  disabled?: boolean;
  bad?: boolean;
  /** Подпись для скринридера, когда рядом нет видимого <label>. */
  ariaLabel?: string;
  className?: string;
};

/** Время выезда листа — роль `--m-dur` (tokens.css) + запас на последний кадр. */
const PICK_UNMOUNT_MS = 380;

/**
 * Лист выбора вместо меню: только браузер на телефоне (≤820). В iOS-приложении (класс
 * `app` на <html>, ТЗ WEB-LOOK Э0.1) меню остаётся прежним — вид приложения не меняется
 * (ТЗ 1.6). Решается в момент нажатия, а не при отрисовке: ширина окна могла смениться.
 */
export function wantsPickSheet(): boolean {
  if (typeof window === 'undefined') return false;
  if (document.documentElement.classList.contains('app')) return false;
  return window.matchMedia('(max-width: 820px)').matches;
}

/**
 * Выпадающий список набора.
 *
 * Заменяет собой голый `<select>`: у системного списка на рабочем столе чужой вид
 * (шрифт и стрелка операционной системы), который в наш кабинет не вписывается.
 * Поведение — Radix: клавиатура, возврат фокуса, переворот у края экрана, скринридер.
 *
 * ВИД ПО РОДНОМУ СПИСКУ iOS (ТЗ WEB-LOOK Э4.3, 30.09.2026): поле — строка-значение с
 * шевроном «›» (`AccountFieldRow`); на телефоне значение выбирают ЛИСТОМ снизу
 * (`AccountPickSheet`: строки 52, у выбранной — галочка акцентом), на широком — меню под
 * полем. Лист рисуется здесь же (`.k-pick` в kit.css — рецепт общего нижнего листа
 * кабинета `.bsh`): пакет набора не может брать `BottomSheet` из приложения.
 *
 * ВАЖНО про соседей (решение Э1.3 плана docs/UI-KIT-PLAN.md): `SmartSelect` (поля форм)
 * и `PopSelect` (разовые выборы на странице) остаются на своих местах и своими именами —
 * правила кабинета про них не отменяются. Этот элемент — их будущая внутренность и
 * замена голому `<select>` там, где ни один из них не подходит.
 */
export function KSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  bad,
  ariaLabel,
  className,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const onOpenChange = (next: boolean) => {
    if (next && wantsPickSheet()) {
      setSheet(true);
      return;
    }
    setMenuOpen(next);
  };

  const closeSheet = useCallback(() => {
    setSheet(false);
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  return (
    <>
      <Select.Root value={value} onValueChange={onChange} disabled={disabled} open={menuOpen} onOpenChange={onOpenChange}>
        <Select.Trigger
          ref={triggerRef}
          aria-label={ariaLabel}
          aria-invalid={bad || undefined}
          className={cx('k k-ctl k-inp k-sel', bad && 'is-bad', className)}
        >
          <Select.Value placeholder={placeholder} />
          <Select.Icon className="k-sel__arrow">
            <Chevron />
          </Select.Icon>
        </Select.Trigger>

        <Select.Portal>
          {/* Меню — слой над контентом, его пункты — ряды пузыря: вид даёт общий пузырь
              (bubble.css), сам список только раскладывает содержимое. */}
          <Select.Content position="popper" sideOffset={6} className="k k-pop bub-layer">
            <Select.Viewport className="k-pop__list bub-rows">
              {options.map((o) => (
                <Select.Item key={o.value} value={o.value} disabled={o.disabled} className="k-pop__item bub-row">
                  {o.icon ? <span className="k-pop__icon">{o.icon}</span> : null}
                  <span className="k-pop__text">
                    <Select.ItemText>{o.label}</Select.ItemText>
                    {o.hint ? <span className="k-pop__hint">{o.hint}</span> : null}
                  </span>
                  <Select.ItemIndicator className="k-pop__tick">
                    <Tick />
                  </Select.ItemIndicator>
                </Select.Item>
              ))}
            </Select.Viewport>
          </Select.Content>
        </Select.Portal>
      </Select.Root>
      {sheet ? (
        <KPickSheet
          title={ariaLabel ?? placeholder}
          options={options}
          value={value}
          onPick={(v) => onChange?.(v)}
          onClose={closeSheet}
        />
      ) : null}
    </>
  );
}

/** Лист выбора (телефон, браузер): затемнение, лист снизу, грабер, крестик слева,
 *  заголовок по центру, строки со значениями — как `AccountPickSheet` приложения.
 *  ОДИН НА ВСЕ ВЫБОРЫ (ТЗ WEB-PARITY-3 6.0 п.4, владелец 02.10.2026): его же открывают
 *  `SmartSelect` (поля форм) и `PopSelect` (разовые выборы) на телефоне в браузере —
 *  системного списка браузера в кабинете больше нет. */
export function KPickSheet({
  title,
  options,
  value,
  onPick,
  onClose,
  note,
}: {
  title?: string;
  options: KOption[];
  value?: string;
  onPick: (value: string) => void;
  onClose: () => void;
  /** ШАПКА СЕКЦИИ над вариантами (серым мелким, как заголовок секции списка): пояснение выбора. Нет — нет. */
  note?: ReactNode;
}) {
  const [show, setShow] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  // Кадр на монтирование в закрытой точке — иначе выезда снизу не видно.
  useEffect(() => {
    const id = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(id);
  }, []);
  const close = useCallback(() => {
    setShow(false);
    setTimeout(onClose, PICK_UNMOUNT_MS);
  }, [onClose]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close]);
  // Фокус — в лист (не на строку: кольцо фокуса на строке после касания пальцем читалось
  // бы как второе «выбрано»); дальше Tab идёт по строкам.
  useEffect(() => {
    boxRef.current?.focus({ preventScroll: true });
  }, []);
  if (typeof document === 'undefined') return null;
  const en = (document.documentElement.lang || '').startsWith('en');
  return createPortal(
    <div className={cx('k k-pick-ov', show && 'is-show')} onClick={close} role="presentation">
      <div
        className="k-pick"
        ref={boxRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="k-pick__grab" aria-hidden />
        <div className="k-pick__head">
          <button type="button" className="k-round k-round--sm k-pick__x" onClick={close} aria-label={en ? 'Close' : 'Закрыть'}>
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
          {title ? <div className="k-pick__title">{title}</div> : null}
        </div>
        {note ? <div className="k-pick__note">{note}</div> : null}
        <div className="k-pick__list" role="listbox" aria-label={title}>
          {options.map((o) => {
            const on = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                disabled={o.disabled}
                className={o.accent ? 'k-pick__row is-accent' : 'k-pick__row'}
                onClick={() => {
                  if (!on) onPick(o.value);
                  close();
                }}
              >
                {o.icon ? <span className="k-pop__icon">{o.icon}</span> : null}
                <span className="k-pick__text">
                  <span className="k-pick__label">{o.label}</span>
                  {o.hint ? <span className="k-pick__hint">{o.hint}</span> : null}
                </span>
                {on ? <Tick /> : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function Chevron() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path d="M3 4.5 6 7.5 9 4.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** Галочка «выбрано» — общий класс `.k-tick` (kit.css, Э4.4). Размер 13 в атрибутах —
 *  прежний вид в iOS-приложении; в браузере размер и цвет даёт класс. */
function Tick() {
  return (
    <svg className="k-tick" width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M2.5 7 5 9.5l5.5-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
